import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import type { PermissionKey, Role, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { describeRejection } from "@/modules/settings/errors";
import {
  groupPermissionsByModule,
  humaniseModule,
} from "@/modules/settings/permissions";

/** `settings.roles.manage` on the system role. See the banner below. */
const LOCKED_PERMISSION: PermissionKey = "settings.roles.manage";
const ROLES_QUERY_KEY = ["settings", "roles"];

/**
 * The role builder (M8-10, M15-2) — the most important screen in Settings.
 *
 * A checkbox toggle is a local draft (`draft[roleId][key]`) until "Save" —
 * that is what lets someone preview a role's whole new shape, across every
 * module's permissions, before one request commits it, rather than firing a
 * PATCH per checkbox. Switching roles or leaving the screen with an unsaved
 * draft loses it, same as before M15-2 — there is just now somewhere to save
 * it to.
 *
 * Every write here can be refused: the Administrator role's checkbox for
 * `settings.roles.manage` is disabled client-side (a courtesy), but the
 * server refuses the same edit on any role if it would leave nobody holding
 * it (FR-ACL-10) — `describeRejection` is what shows that refusal when the
 * courtesy could not have caught it (a custom role that happens to be the
 * only other holder, say).
 */
export function RoleBuilder() {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: () => data.settings.listRoles(),
  });
  const permissionsQuery = useQuery({
    queryKey: ["settings", "permissions"],
    queryFn: () => data.settings.listPermissions(),
  });

  const [selection, setSelection] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  /**
   * `draft[roleId][key]` overrides `role.permissions.includes(key)` for one
   * checkbox. Absent means "use the role's own permission list" — so
   * opening a role nobody has touched yet renders it exactly as `listRoles`
   * returned it, with nothing precomputed for every role up front.
   */
  const [draft, setDraft] = useState<Record<string, Record<string, boolean>>>(
    {},
  );
  const [permsRejection, setPermsRejection] = useState<
    Record<string, WriteRejection>
  >({});

  const [renaming, setRenaming] = useState<{
    id: string;
    value: string;
  } | null>(null);
  const [renameRejection, setRenameRejection] = useState<WriteRejection | null>(
    null,
  );

  const [creating, setCreating] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [createRejection, setCreateRejection] = useState<WriteRejection | null>(
    null,
  );

  const [deleteRejection, setDeleteRejection] = useState<
    Record<string, WriteRejection>
  >({});

  function invalidateRoles() {
    void queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
  }

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      data.settings.createRole({ name, permissions: [] }),
    onSuccess: (result) => {
      if (!result.ok) {
        setCreateRejection(result.rejection);
        return;
      }
      setCreating(false);
      setNewRoleName("");
      setCreateRejection(null);
      setSelection(result.data.id);
      invalidateRoles();
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      data.settings.renameRole(id, name),
    onSuccess: (result) => {
      if (!result.ok) {
        setRenameRejection(result.rejection);
        return;
      }
      setRenaming(null);
      setRenameRejection(null);
      invalidateRoles();
    },
  });

  const permsMutation = useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      data.settings.setRolePermissions(id, permissions),
    onSuccess: (result, variables) => {
      if (!result.ok) {
        setPermsRejection((prev) => ({
          ...prev,
          [variables.id]: result.rejection,
        }));
        return;
      }
      setDraft((prev) => {
        const next = { ...prev };
        delete next[variables.id];
        return next;
      });
      setPermsRejection((prev) => {
        const next = { ...prev };
        delete next[variables.id];
        return next;
      });
      invalidateRoles();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => data.settings.deleteRole(id),
    onSuccess: (result, id) => {
      if (!result.ok) {
        setDeleteRejection((prev) => ({ ...prev, [id]: result.rejection }));
        return;
      }
      setDeleteRejection((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSelection((current) => (current === id ? null : current));
      invalidateRoles();
    },
  });

  if (rolesQuery.isPending || permissionsQuery.isPending) {
    return <TableSkeleton rows={6} />;
  }

  if (rolesQuery.isError) {
    return (
      <LoadFailed
        message={rolesQuery.error.message}
        onRetry={() => {
          void rolesQuery.refetch();
        }}
      />
    );
  }
  if (permissionsQuery.isError) {
    return (
      <LoadFailed
        message={permissionsQuery.error.message}
        onRetry={() => {
          void permissionsQuery.refetch();
        }}
      />
    );
  }

  const roles = rolesQuery.data.items;
  const selectedRole =
    roles.find((role) => role.id === selection) ?? roles[0] ?? null;

  const groups = groupPermissionsByModule(permissionsQuery.data);
  const selectedRolePermsRejection =
    selectedRole === null ? undefined : permsRejection[selectedRole.id];

  function isChecked(role: Role, key: PermissionKey): boolean {
    const override = draft[role.id]?.[key];
    return override ?? role.permissions.includes(key);
  }

  function isDirty(role: Role): boolean {
    return Object.keys(draft[role.id] ?? {}).length > 0;
  }

  /** The permission list a "Save" for this role would send. */
  function effectivePermissions(role: Role): PermissionKey[] {
    const override = draft[role.id];
    if (!override) return role.permissions;
    const set = new Set(role.permissions);
    for (const [key, value] of Object.entries(override)) {
      if (value) set.add(key);
      else set.delete(key);
    }
    return [...set];
  }

  function toggle(role: Role, key: PermissionKey) {
    // The administrator role is `isSystem`: it cannot be deleted, and it
    // cannot have `settings.roles.manage` taken away, because that
    // permission is what lets anyone fix a permission mistake at all — take
    // it from the last role holding it and nobody, including an owner, can
    // grant it back (M15-8, FR-ACL-10). Refusing the click silently would
    // look like a bug; the checkbox stays visibly locked instead. Every
    // other role's edit still goes to the server, which enforces the same
    // rule for whichever role happens to be the last holder.
    if (role.isSystem && key === LOCKED_PERMISSION) return;

    setDraft((prev) => {
      const roleDraft = prev[role.id] ?? {};
      const next = !isChecked(role, key);
      return { ...prev, [role.id]: { ...roleDraft, [key]: next } };
    });
  }

  /** Grant or clear a whole module at once, as a draft like any one tick. */
  function setAll(role: Role, keys: readonly PermissionKey[], value: boolean) {
    setDraft((prev) => {
      const roleDraft = { ...(prev[role.id] ?? {}) };
      for (const key of keys) {
        if (role.isSystem && key === LOCKED_PERMISSION) continue;
        if (role.permissions.includes(key) === value) delete roleDraft[key];
        else roleDraft[key] = value;
      }
      return { ...prev, [role.id]: roleDraft };
    });
  }

  function discardDraft(role: Role) {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[role.id];
      return next;
    });
    setPermsRejection((prev) => {
      const next = { ...prev };
      delete next[role.id];
      return next;
    });
  }

  const needle = search.trim().toLowerCase();
  const shownGroups = groups
    .map((group) => ({
      ...group,
      shown:
        needle === "" ||
        humaniseModule(group.module).toLowerCase().includes(needle)
          ? group.permissions
          : group.permissions.filter((permission) =>
              `${permission.label} ${permission.description ?? ""}`
                .toLowerCase()
                .includes(needle),
            ),
    }))
    .filter((group) => group.shown.length > 0);

  return (
    <div className="flex h-full min-h-0 flex-col md:flex-row">
      <aside className="max-h-64 w-full shrink-0 overflow-auto border-b p-3 md:max-h-none md:w-60 md:border-r md:border-b-0">
        <div className="flex h-8 items-center justify-between pr-1 pl-2">
          <span className="text-muted-foreground text-xs font-medium">
            Roles
          </span>
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => {
              setCreating((prev) => !prev);
              setCreateRejection(null);
            }}
          >
            <Plus />
            <span className="sr-only">New role</span>
          </Button>
        </div>

        {creating ? (
          <form
            className="space-y-2 px-1 py-2"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = newRoleName.trim();
              if (trimmed === "") return;
              createMutation.mutate(trimmed);
            }}
          >
            <Input
              autoFocus
              className="h-8"
              placeholder="Role name"
              value={newRoleName}
              onChange={(event) => {
                setNewRoleName(event.target.value);
              }}
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={newRoleName.trim() === "" || createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                Create
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCreating(false);
                  setNewRoleName("");
                  setCreateRejection(null);
                }}
              >
                Cancel
              </Button>
            </div>
            {createRejection ? (
              <p className="text-destructive text-xs">
                {describeRejection(createRejection)}
              </p>
            ) : null}
          </form>
        ) : null}

        <ul className="space-y-0.5">
          {roles.map((role) => {
            const isRenaming = renaming?.id === role.id;
            const isSelected = selectedRole?.id === role.id;
            const roleDeleteRejection = deleteRejection[role.id];
            return (
              <li key={role.id}>
                {isRenaming ? (
                  <form
                    className="space-y-2 px-1 py-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const trimmed = renaming.value.trim();
                      if (trimmed === "") return;
                      renameMutation.mutate({ id: role.id, name: trimmed });
                    }}
                  >
                    <Input
                      autoFocus
                      className="h-8"
                      aria-label={`New name for ${role.name}`}
                      value={renaming.value}
                      onChange={(event) => {
                        setRenaming({ id: role.id, value: event.target.value });
                      }}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        disabled={
                          renaming.value.trim() === "" ||
                          renameMutation.isPending
                        }
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setRenaming(null);
                          setRenameRejection(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    {renameRejection ? (
                      <p className="text-destructive text-xs">
                        {describeRejection(renameRejection)}
                      </p>
                    ) : null}
                  </form>
                ) : (
                  <div
                    className={cn(
                      "group flex h-8 items-center rounded-md pr-1 text-sm",
                      isSelected
                        ? "bg-accent text-foreground font-medium"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )}
                  >
                    <button
                      type="button"
                      aria-current={isSelected ? "true" : undefined}
                      onClick={() => {
                        setSelection(role.id);
                      }}
                      onDoubleClick={() => {
                        if (role.isSystem) return;
                        setRenaming({ id: role.id, value: role.name });
                        setRenameRejection(null);
                      }}
                      className="focus-visible:ring-ring/50 flex h-full min-w-0 flex-1 items-center gap-1.5 rounded-md pl-2 text-left outline-none focus-visible:ring-[3px]"
                    >
                      <span className="truncate">{role.name}</span>
                      {role.isSystem ? (
                        <ShieldCheck
                          className="text-muted-foreground size-3.5 shrink-0"
                          aria-label="System role"
                        />
                      ) : null}
                      {isDirty(role) ? (
                        <span
                          className="bg-primary size-1.5 shrink-0 rounded-full"
                          aria-label="Unsaved changes"
                        />
                      ) : null}
                    </button>
                    <span className="text-muted-foreground px-1.5 text-xs font-normal tabular-nums">
                      {role.permissions.length}
                    </span>
                    {role.isSystem ? (
                      // The width of the menu button the other rows have, so
                      // the counts line up in one column.
                      <span className="size-6 shrink-0" aria-hidden />
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            size="icon-xs"
                            variant="ghost"
                            className="text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 max-md:opacity-100"
                            aria-label={`Actions for ${role.name}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onSelect={() => {
                              setRenaming({ id: role.id, value: role.name });
                              setRenameRejection(null);
                            }}
                          >
                            <Pencil />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            disabled={
                              deleteMutation.isPending &&
                              deleteMutation.variables === role.id
                            }
                            onSelect={() => {
                              deleteMutation.mutate(role.id);
                            }}
                          >
                            <Trash2 />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}
                {roleDeleteRejection ? (
                  <p className="text-destructive px-2 py-1 text-xs">
                    {describeRejection(roleDeleteRejection)}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </aside>

      <div className="min-h-0 flex-1 overflow-auto">
        {selectedRole === null ? (
          <p className="text-muted-foreground p-6 text-sm">No roles exist.</p>
        ) : (
          <div className="mx-auto max-w-6xl px-4 pb-10 sm:px-8">
            <header className="pt-6 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-xl font-semibold tracking-tight">
                  {selectedRole.name}
                </h3>
                {selectedRole.isSystem ? (
                  <Badge
                    variant="secondary"
                    className="bg-tint-gray text-tint-gray-foreground font-normal"
                  >
                    <ShieldCheck aria-hidden />
                    System role — cannot be deleted
                  </Badge>
                ) : null}
              </div>
              {selectedRole.description ? (
                <p className="text-muted-foreground mt-1 text-sm">
                  {selectedRole.description}
                </p>
              ) : null}
              <p className="text-muted-foreground mt-1 text-xs tabular-nums">
                {effectivePermissions(selectedRole).length} of{" "}
                {permissionsQuery.data.length} permissions granted
              </p>
            </header>

            {/* Stays in view: the ticks that made it appear may be a long way
                down the page from the button that keeps them. */}
            <div className="bg-background sticky top-0 z-10 space-y-2 border-b py-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-40 flex-1">
                  <Search
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    aria-label="Search permissions"
                    placeholder="Search permissions"
                    className="h-8 pl-8"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                    }}
                  />
                </div>
                {isDirty(selectedRole) ? (
                  <>
                    <span className="text-muted-foreground text-xs">
                      Unsaved changes
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        discardDraft(selectedRole);
                      }}
                    >
                      Revert
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={permsMutation.isPending}
                      onClick={() => {
                        permsMutation.mutate({
                          id: selectedRole.id,
                          permissions: effectivePermissions(selectedRole),
                        });
                      }}
                    >
                      {permsMutation.isPending ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : null}
                      Save permissions
                    </Button>
                  </>
                ) : null}
              </div>
              {selectedRolePermsRejection ? (
                <p role="alert" className="text-destructive text-sm">
                  {describeRejection(selectedRolePermsRejection)}
                </p>
              ) : null}
            </div>

            {shownGroups.length === 0 ? (
              <p className="text-muted-foreground py-8 text-sm">
                No permission matches “{search.trim()}”.
              </p>
            ) : null}

            {shownGroups.map((group) => {
              const keys = group.permissions.map(
                (permission) => permission.key,
              );
              const granted = keys.filter((key) =>
                isChecked(selectedRole, key),
              ).length;
              const all = granted === keys.length;
              return (
                <section key={group.module} className="pt-6">
                  <div className="flex items-center gap-2 border-b pb-1.5">
                    <h4 className="text-sm font-semibold">
                      {humaniseModule(group.module)}
                    </h4>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {granted} of {keys.length}
                    </span>
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      className="text-muted-foreground ml-auto"
                      onClick={() => {
                        setAll(selectedRole, keys, !all);
                      }}
                    >
                      {all ? "Clear all" : "Select all"}
                    </Button>
                  </div>
                  {/* As many columns as fit at a readable width: one on a
                      phone, two or three beside the role list. Each row of
                      the grid is as tall as its longest description, so the
                      ticks stay in line across it. */}
                  <ul className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-x-6 pt-1">
                    {group.shown.map((permission) => {
                      const locked =
                        selectedRole.isSystem &&
                        permission.key === LOCKED_PERMISSION;
                      const checkboxId = `${selectedRole.id}-${permission.key}`;

                      return (
                        <li
                          key={permission.key}
                          className="hover:bg-accent/50 -mx-2 flex items-start gap-3 rounded-md px-2 py-2"
                        >
                          <Checkbox
                            id={checkboxId}
                            className="mt-0.5"
                            checked={isChecked(selectedRole, permission.key)}
                            disabled={locked}
                            onCheckedChange={() => {
                              toggle(selectedRole, permission.key);
                            }}
                          />
                          <div className="grid min-w-0 gap-0.5">
                            <Label
                              htmlFor={checkboxId}
                              className="flex items-center gap-1 text-sm leading-5 font-normal"
                            >
                              {permission.label}
                              {locked ? (
                                <Lock
                                  className="text-muted-foreground size-3"
                                  aria-hidden
                                />
                              ) : null}
                            </Label>
                            {permission.description ? (
                              <p className="text-muted-foreground text-xs leading-5">
                                {permission.description}
                              </p>
                            ) : null}
                            {locked ? (
                              <p className="text-muted-foreground text-xs leading-5">
                                Every role holding administrator status keeps
                                this permission — removing it would leave nobody
                                able to grant it back (M15-8).
                              </p>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
